import React, { useState } from "react";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { withRouter } from "react-router";
import { FormattedMessage } from "react-intl";
import Alert from "react-s-alert";
import { Box, Flex } from "ui-components";
import Spinner, { styles } from "components/Spinner";
import { COLORS, iconThemes } from "ui-components/theme";
import isFunction from "lodash/isFunction";
import { ReactComponent as ApplePayIcon } from "assets/icons/apple-pay.svg";
import { ReactComponent as GooglePayIcon } from "assets/icons/google-pay.svg";

function StripePaymentForm(props) {
  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState(null);
  const [clientSideError, setClientSideError] = useState(null);
  const [processing, setProcessing] = useState("");
  const [method, setMethod] = useState("card");
  const [disabled, setDisabled] = useState(true);
  const stripe = useStripe();
  const elements = useElements();
  const cardStyle = {
    hidePostalCode: true,
    style: {
      base: {
        color: iconThemes.navy,
        width: "100%",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: COLORS.silver[0]
        }
      },
      invalid: {
        color: iconThemes.red,
        iconColor: iconThemes.red
      }
    }
  };
  // Extract required attributes from props
  const { paymentRequest, appSettings } = props;
  const stripeFormData = paymentRequest?.form_data?.stripe;
  const intentConfirmation = stripeFormData?.intent_confirmation || "";
  const clientSecret = stripeFormData?.intent?.client_secret || "";
  const amount = stripeFormData?.intent?.amount || 0;
  const enableStripeBillingDetails = appSettings?.enable_stripe_billing_details;
  const enableApplePay = appSettings?.enable_apple_pay;
  const enableGooglePay = appSettings?.enable_google_pay;

  const paymentElementOptions = {
    business: {
      name: "Circles.Life"
    },
    fields: {
      billingDetails: {
        address: {
          country: enableStripeBillingDetails ? "auto" : "never"
        }
      }
    },
    defaultValues: {
      billingDetails: {
        address: {
          country: "SG"
        }
      }
    },
    wallets: {
      applePay: enableApplePay ? "auto" : "never",
      googlePay: enableGooglePay ? "auto" : "never"
    }
  };

  const methodButtonColors = {
    paynow: "#8a2c7c",
    grabpay: "#08af55"
  };

  const buttonColor = methodButtonColors[method] || iconThemes.black;

  const paymentButtonStyle = {
    marginTop: "18px",
    width: "100%",
    color: "#fff",
    backgroundColor: buttonColor,
    borderColor: buttonColor,
    display: "inline-block",
    fontWeight: "500",
    lineHeight: "1.25",
    textAlign: "center",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    cursor: "pointer",
    userSelect: "none",
    border: "1px solid",
    padding: "0.5rem 1rem",
    fontSize: "1.25rem",
    height: "50px",
    borderRadius: "0.25rem"
  };

  const handleChange = event => {
    // Listen for changes in the PaymentElement
    // and display any errors as the customer types their card details
    const method = event?.value?.type || "card";
    // card | google_pay | apple_pay
    setMethod(method);
    if (method === "card") {
      setDisabled(event.empty || event.error || !event.complete);
      setClientSideError(event.error ? event.error.message : "");
    } else {
      setDisabled(false);
    }
  };

  const handleSubmit = async ev => {
    ev.preventDefault();
    if (!stripe) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setDisabled(true);
    setProcessing(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      // Use default error message as the error.message is not user friendly
      const errorMsg = "Error Processing Payment. Please Retry.";
      setProcessing(false);
      setError(errorMsg);
      Alert.error(errorMsg);
      setTimeout(() => {
        window.location.reload();
      }, 3000);
      return;
    }

    const return_url = `${window.location.origin}${props.returnPath}`;

    // Use the clientSecret and Elements instance to confirm the setup
    const confirmPayload = {
      elements,
      clientSecret,
      confirmParams: {
        return_url,
        payment_method_data: {
          billing_details: {
            address: {
              country: "SG"
            }
          }
        }
      },
      // Uncomment below if you only want redirect for redirect-based payments
      redirect: "if_required"
    };

    let stripeResponse;
    if (intentConfirmation === "confirmCardSetup") {
      stripeResponse = await stripe.confirmSetup(confirmPayload);
    } else {
      stripeResponse = await stripe.confirmPayment(confirmPayload);
    }

    setProcessing(false);
    if (
      !stripeResponse ||
      stripeResponse.error ||
      stripeResponse?.paymentIntent?.status === "requires_payment_method"
    ) {
      const submitError = `Payment failed! ${stripeResponse?.error?.message ||
        stripeResponse?.paymentIntent?.last_payment_error?.message}`;
      setError(submitError);
      Alert.error(submitError);
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } else {
      setSucceeded(true);
      isFunction(props.onPaymentSuccess) && props.onPaymentSuccess();
      props.returnPath && props.history.push(props.returnPath);
    }
  };

  return (
    <Flex width={["100%", "80%", "70%"]} mb={3} mx={"auto"}>
      <Box width={"100%"}>
        <Box>
          <form
            id="payment-form"
            data-testid="payment-form"
            onSubmit={handleSubmit}
          >
            <Box style={{ display: processing ? "block" : "none" }}>
              {<Spinner sx={styles.modalContainer} />}
            </Box>

            <Box
              id="stripe-wrap-1"
              style={{ display: processing ? "none" : "block" }}
            >
              <PaymentElement
                id="payment-element"
                options={paymentElementOptions}
                onChange={handleChange}
                className="sr-input sr-payment-element"
              />

              {clientSideError && (
                <div
                  className="card-error"
                  role="alert"
                  style={{
                    color: cardStyle.style.invalid.color,
                    margin: "7px 0"
                  }}
                >
                  {clientSideError}
                </div>
              )}
              {
                <button
                  disabled={processing || disabled || succeeded}
                  id="submit"
                  data-testid="stripe-pay-button"
                  className="stripe-pay-button"
                  style={paymentButtonStyle}
                >
                  <Flex
                    id="button-text"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <FormattedMessage
                      html
                      id={amount > 0 ? "payment.pay_amount" : "payment.confirm"}
                    />
                    {amount > 0 && (amount / 100).toFixed(2)}
                    {method === "google_pay" && (
                      <>
                        <span>&nbsp;with</span>
                        <GooglePayIcon />
                      </>
                    )}
                    {method === "apple_pay" && (
                      <>
                        <span>&nbsp;with</span>
                        <ApplePayIcon />
                      </>
                    )}
                    {method === "paynow" && (
                      <>
                        <span>&nbsp;with</span>
                        <b>&nbsp;PayNow</b>
                      </>
                    )}
                    {method === "grabpay" && (
                      <>
                        <span>&nbsp;with</span>
                        <b>&nbsp;GrabPay</b>
                      </>
                    )}
                  </Flex>
                </button>
              }

              {error && (
                <div
                  className="card-error"
                  role="alert"
                  style={{
                    color: cardStyle.style.invalid.color,
                    margin: "7px 0"
                  }}
                >
                  {error}
                </div>
              )}
            </Box>
          </form>
        </Box>
      </Box>
    </Flex>
  );
}

function StripePayment(props) {
  const { paymentRequest } = props;
  const stripe_form_data = paymentRequest?.form_data?.stripe;
  const publishable_api_key = stripe_form_data?.publishable_api_key || "";
  const intent_confirmation = stripe_form_data?.intent_confirmation || "";
  const { amount, setup_future_usage, payment_method_types } =
    stripe_form_data?.intent || {};

  const stripeOptions = {
    mode: amount ? "payment" : "setup",
    currency: "sgd",
    amount: amount || 0,
    appearance: {
      theme: "stripe"
    },
    loader: "always"
  };

  if (!setup_future_usage && intent_confirmation !== "confirmCardSetup") {
    stripeOptions.payment_method_types = payment_method_types || ["card"];
  } else {
    stripeOptions.setup_future_usage = setup_future_usage || "off_session";
  }

  return (
    <Elements stripe={loadStripe(publishable_api_key)} options={stripeOptions}>
      <StripePaymentForm {...props} />
    </Elements>
  );
}

export default withRouter(StripePayment);
